/**
 * Instruction steps (DEV-1289) — the checklist on a mini-project task.
 *
 * Stored as JSONB on mini_project_tasks.instruction_steps. This module is the
 * single shape definition shared by the authoring tray (edit) and the
 * execution tray (detail), so the two surfaces stay consistent — the same
 * intent behind borrowing the rhythm-task step elements (label + masked link +
 * required) without merging the two architectures.
 *
 * Shape v2 (this file): [{ id, label, url?, urlLabel?, required, checked }].
 * Back-compatible with the legacy v1 shape [{ label, checked }] — parse()
 * tolerates missing id/required and synthesizes a stable id.
 */

export interface InstructionStep {
  /** Stable id so the assignee's check-toggle can target a step without
   *  depending on array position (labels/order can change in authoring). */
  id: string;
  label: string;
  /** Optional external link (http/https only). The raw URL. */
  url?: string;
  /** Friendly mask shown instead of the raw URL; raw URL stays on hover. */
  urlLabel?: string;
  /** When true, the step must be checked before the task can be marked done. */
  required: boolean;
  checked: boolean;
}

function makeId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `step-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Parse the JSONB string into typed steps, tolerating the legacy v1 shape. */
export function parseInstructionSteps(json?: string | null): InstructionStep[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((s: any) => ({
        id: typeof s?.id === "string" && s.id ? s.id : makeId(),
        label: String(s?.label ?? "").trim(),
        url: typeof s?.url === "string" && s.url.trim() ? s.url.trim() : undefined,
        urlLabel:
          typeof s?.urlLabel === "string" && s.urlLabel.trim() ? s.urlLabel.trim() : undefined,
        required: !!s?.required,
        checked: !!s?.checked,
      }))
      .filter((s: InstructionStep) => s.label.length > 0);
  } catch {
    return [];
  }
}

export function serializeInstructionSteps(steps: InstructionStep[]): string {
  return JSON.stringify(
    steps
      .filter((s) => s.label.trim().length > 0)
      .map((s) => ({
        id: s.id || makeId(),
        label: s.label.trim(),
        url: s.url?.trim() || undefined,
        urlLabel: s.urlLabel?.trim() || undefined,
        required: !!s.required,
        checked: !!s.checked,
      })),
  );
}

/** A fresh blank step for the authoring editor. */
export function newInstructionStep(): InstructionStep {
  return { id: makeId(), label: "", required: false, checked: false };
}

/** Only http/https are safe to render as an href — blocks javascript:/data:
 *  scheme injection (XSS). Empty/invalid URLs are not safe (render as text). */
export function isSafeHttpUrl(u?: string | null): boolean {
  if (!u) return false;
  try {
    const p = new URL(u.trim());
    return p.protocol === "http:" || p.protocol === "https:";
  } catch {
    return false;
  }
}

/** True when every REQUIRED step is checked (or there are no required steps). */
export function requiredStepsSatisfied(steps: InstructionStep[]): boolean {
  return steps.every((s) => !s.required || s.checked);
}
